import AudioViewer from '@wandb/weave/components/Panel2/AudioViewer';
import * as React from 'react';

import {labelComponent} from '../util/media';
import {useDownloadFile} from '../util/requests';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {getAudioMedia, type MediaCardProps} from './MediaCard';
import MessageMediaNotFound from './MessageMediaNotFound';
import {SingleRunLink} from './MultiRunWorkspace/SingleRunMode';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';

// This panel shows the audio for a single run, using wavesurfer.js to display the waveform

type AudioCardProps = MediaCardProps;

export const AudioCard: React.FunctionComponent<AudioCardProps> = React.memo(
  props => {
    const {
      mediaKey,
      mediaIndex,
      run,
      runSignature,
      globalStep,
      height,
      tileMedia,
      loading,
      customRunName,
      tileMediaSpec,
      getMediaTracking,
    } = props;

    const downloadFile = useDownloadFile();

    const media = getAudioMedia(
      {historyRow: tileMedia?.historyRow, mediaKey},
      mediaIndex
    );

    const titleLink = (
      <SingleRunLink
        {...runSignature}
        linkText={customRunName ?? run.displayName}
      />
    );

    const caption: string | undefined =
      media?.caption ?? tileMedia?.historyRow[mediaKey]?.captions?.[mediaIndex];

    const ariaMetadata = `Run ${run.displayName}, Key ${mediaKey}, Step ${globalStep}, Index ${mediaIndex}, Caption ${caption}`;

    const {trackSuccess, trackError} = useGetTrackingCallbacks(
      tileMedia,
      tileMediaSpec,
      getMediaTracking
    );

    return (
      <AudioViewer
        ariaMetadata={ariaMetadata}
        audioSrc={tileMedia?.objectURL}
        caption={caption}
        height={height}
        mediaFailedToLoad={tileMedia == null}
        headerElement={labelComponent(props, tileMedia?.step, titleLink)}
        failedLoadElement={
          loading ? (
            <Loader name="panel-content-loader" size="small" />
          ) : (
            <MessageMediaNotFound
              mediaKey={mediaKey}
              stepIndex={globalStep}
              mediaIndex={mediaIndex}
              mediaType="audio"
            />
          )
        }
        downloadFile={() =>
          downloadFile(runSignature, tileMedia?.filePath ?? '')
        }
        trackSuccess={trackSuccess}
        trackError={trackError}
      />
    );
  }
);

export default AudioCard;
